import React, { useEffect, useState, useMemo } from 'react';
import { X, Plus, RefreshCw, Edit2, Trash2, Save, Clock, Loader2 } from 'lucide-react';
import DataGrid from '../../components/datagrid/DataGrid';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import './MemberShiftsModal.css';

const DAY_NAMES = {
  1: 'Pazartesi', 2: 'Salı', 3: 'Çarşamba',
  4: 'Perşembe', 5: 'Cuma', 6: 'Cumartesi', 7: 'Pazar'
};

const formatTimeSpan = (ts) => {
  if (!ts) return '--:--';
  if (typeof ts === 'string') {
    const parts = ts.split(':');
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return ts;
};

const MemberShiftsModal = ({ member, onClose }) => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shiftForm, setShiftForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [sortField, setSortField] = useState('dayOfWeek');
  const [sortOrder, setSortOrder] = useState('asc');

  const loadShifts = async () => {
    if (!member?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.MEMBER_SHIFTS(member.id));
      if (res.success) {
        setShifts(res.data || []);
      } else {
        setError(res.error || 'Vardiyalar yüklenemedi');
      }
    } catch {
      setError('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShifts();
  }, [member?.id]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const sorted = useMemo(() => {
    if (!sortField) return shifts;
    const arr = [...shifts].sort((a, b) => {
      const av = a?.[sortField];
      const bv = b?.[sortField];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv), 'tr');
    });
    return sortOrder === 'desc' ? arr.reverse() : arr;
  }, [shifts, sortField, sortOrder]);

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
  };

  const openAdd = () => {
    setShiftForm({ id: null, dayOfWeek: 1, startTime: '09:00', endTime: '18:00', isActive: true });
  };

  const openEdit = (row) => {
    setShiftForm({
      id: row.id,
      dayOfWeek: row.dayOfWeek,
      startTime: formatTimeSpan(row.startTime),
      endTime: formatTimeSpan(row.endTime),
      isActive: row.isActive,
    });
  };

  const handleSave = async () => {
    if (!member || !shiftForm) return;
    setSaving(true);
    try {
      const body = {
        dayOfWeek: parseInt(shiftForm.dayOfWeek),
        startTime: shiftForm.startTime + ':00',
        endTime: shiftForm.endTime + ':00',
        isActive: shiftForm.isActive,
      };

      let res;
      if (shiftForm.id) {
        res = await apiService.put(
          API_ENDPOINTS.FLEET_PANEL.MEMBER_SHIFT_UPDATE(member.id, shiftForm.id),
          body
        );
      } else {
        res = await apiService.post(
          API_ENDPOINTS.FLEET_PANEL.MEMBER_SHIFTS(member.id),
          body
        );
      }

      if (res.success) {
        fleetToast.success(shiftForm.id ? 'Vardiya güncellendi' : 'Vardiya eklendi');
        setShiftForm(null);
        await loadShifts();
      } else {
        fleetToast.error(res.error || 'İşlem başarısız');
      }
    } catch {
      fleetToast.error('Vardiya kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!member) return;
    setSaving(true);
    try {
      const res = await apiService.delete(
        API_ENDPOINTS.FLEET_PANEL.MEMBER_SHIFT_DELETE(member.id, row.id)
      );
      if (res.success) {
        fleetToast.success('Vardiya silindi');
        await loadShifts();
      } else {
        fleetToast.error(res.error || 'Silme başarısız');
      }
    } catch {
      fleetToast.error('Vardiya silinemedi');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'dayOfWeek',
      header: 'Gün',
      sortable: true,
      render: (r) => DAY_NAMES[r.dayOfWeek] || '-'
    },
    {
      field: 'startTime',
      header: 'Başlangıç',
      sortable: true,
      className: 'mono',
      render: (r) => formatTimeSpan(r.startTime)
    },
    {
      field: 'endTime',
      header: 'Bitiş',
      sortable: true,
      className: 'mono',
      render: (r) => (
        <span>
          {formatTimeSpan(r.endTime)}
          {r.crossesMidnight ? (
            <span style={{
              marginLeft: 6,
              padding: '1px 6px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              color: '#4f46e5',
              background: 'rgba(99,102,241,0.12)',
            }}>+1 gün</span>
          ) : null}
        </span>
      )
    },
    {
      field: 'durationMinutes',
      header: 'Süre',
      sortable: true,
      render: (r) => {
        const total = r.durationMinutes || 0;
        return `${Math.floor(total / 60)}s ${total % 60}dk`;
      }
    },
    {
      field: 'isActive',
      header: 'Durum',
      sortable: true,
      render: (r) => (
        <span className={`ms-status-badge ${r.isActive ? 'active' : 'inactive'}`}>
          {r.isActive ? 'Aktif' : 'Pasif'}
        </span>
      )
    }
  ], []);

  const renderActions = (row) => (
    <div className="ms-row-actions">
      <button className="ms-action-btn ms-action-edit" title="Düzenle" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
        <Edit2 size={15} />
      </button>
      <button className="ms-action-btn ms-action-delete" title="Sil" disabled={saving} onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
        <Trash2 size={15} />
      </button>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="ms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms-modal-header">
          <div className="ms-modal-title">
            <div className="ms-modal-icon"><Clock size={18} /></div>
            <div>
              <h2>Vardiya Yönetimi</h2>
              <span className="ms-modal-sub">{member?.courierName || 'Kurye'}</span>
            </div>
          </div>
          <div className="ms-modal-header-actions">
            <button className="ms-btn ms-btn-secondary" onClick={loadShifts} disabled={loading}>
              <RefreshCw size={15} className={loading ? 'spinning' : ''} />
              Yenile
            </button>
            <button className="ms-btn ms-btn-success" onClick={openAdd} disabled={loading || saving}>
              <Plus size={15} />
              Yeni Vardiya
            </button>
            <button className="ms-modal-close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ms-modal-body">
          {error && <div className="ms-error">{error}</div>}

          <DataGrid
            data={sorted}
            columns={columns}
            loading={loading}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            onRowDoubleClick={openEdit}
            renderActions={renderActions}
            emptyIcon={Clock}
            emptyMessage="Vardiya bulunamadı"
          />

          {shiftForm && (
            <div className="ms-form-card">
              <div className="ms-form-card-header">
                <span className="ms-form-card-title">{shiftForm.id ? 'Vardiya Düzenle' : 'Yeni Vardiya'}</span>
                <button className="ms-form-card-close" onClick={() => setShiftForm(null)}><X size={16} /></button>
              </div>
              <div className="ms-form-fields">
                <div className="ms-form-group">
                  <label>Gün</label>
                  <select value={shiftForm.dayOfWeek} onChange={(e) => setShiftForm(f => ({ ...f, dayOfWeek: e.target.value }))}>
                    {Object.entries(DAY_NAMES).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="ms-form-group">
                  <label>Başlangıç</label>
                  <input type="time" value={shiftForm.startTime} onChange={(e) => setShiftForm(f => ({ ...f, startTime: e.target.value }))} />
                </div>
                <div className="ms-form-group">
                  <label>Bitiş</label>
                  <input type="time" value={shiftForm.endTime} onChange={(e) => setShiftForm(f => ({ ...f, endTime: e.target.value }))} />
                </div>
                <div className="ms-form-group">
                  <label>Durum</label>
                  <div className="ms-toggle">
                    <button className={`ms-toggle-btn ${shiftForm.isActive ? 'on' : ''}`} onClick={() => setShiftForm(f => ({ ...f, isActive: true }))}>Aktif</button>
                    <button className={`ms-toggle-btn ${!shiftForm.isActive ? 'on off' : ''}`} onClick={() => setShiftForm(f => ({ ...f, isActive: false }))}>Pasif</button>
                  </div>
                </div>
                {shiftForm.startTime && shiftForm.endTime && shiftForm.endTime < shiftForm.startTime && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#4f46e5', marginTop: 6, gridColumn: '1 / -1' }}>
                    Vardiya ertesi gün {shiftForm.endTime}'te biter.
                  </div>
                )}
                {shiftForm.startTime && shiftForm.endTime && shiftForm.endTime === shiftForm.startTime && (
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0284c7', marginTop: 6, gridColumn: '1 / -1' }}>
                    Kesintisiz 24 saat vardiya.
                  </div>
                )}
              </div>
              <div className="ms-form-card-footer">
                <button className="ms-btn ms-btn-secondary" onClick={() => setShiftForm(null)} disabled={saving}>Vazgeç</button>
                <button className="ms-btn ms-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 size={15} className="spinning" /> : <Save size={15} />}
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="ms-modal-footer">
          <button className="ms-btn ms-btn-secondary" onClick={onClose}>Kapat</button>
        </div>
      </div>
    </div>
  );
};

export default MemberShiftsModal;
